import { useMemo } from "react";
import {
  IconNameEnum,
  useHotelsStyles,
  useModuleBEM,
} from "@b2bportal/core-themes";
import {
  CoreHotelsComponent,
  type PlatformComponentProps,
} from "@b2bportal/core-types";
import type { TripAdvisorReviews } from "@b2bportal/lodging-api";
import {
  CustomerReviews,
  HotelShopSection,
  useCustomerReviewsSection,
} from "@tenant/hotels";
import {
  Dialog,
  DialogContent,
  DialogTrigger,
  Icon,
  TripadvisorRatingIcon,
} from "@tenant/ui";
import clsx from "clsx";
import defaultStyles from "./CustomerReviewsSection.module.scss";

export interface CustomerReviewsSectionComponentProps
  extends PlatformComponentProps {
  reviews: TripAdvisorReviews;
}

const COMPONENT_KEY = CoreHotelsComponent.CustomerReviewsSection;

export const CustomerReviewsSection = (
  props: CustomerReviewsSectionComponentProps
) => {
  const styles = useHotelsStyles(COMPONENT_KEY, defaultStyles);
  const [block, cn] = useModuleBEM(styles, COMPONENT_KEY);

  const { children, className } = props;

  const { context, text } = useCustomerReviewsSection(props);

  const summary = useMemo(
    () => (
      <div className={cn("summary")}>
        <div className={cn("rating-icon")}>
          <Icon
            className={cn("tripadvisor-icon")}
            iconName={IconNameEnum.tripadvisor}
          />
          <TripadvisorRatingIcon scoreOutOfFive={context.overallRating} />
        </div>
        <div className={cn("average-rating")}>{text.ratingAverage}</div>
        <div className={cn("review-count")}>{text.reviewCount}</div>
      </div>
    ),
    [cn, context.overallRating, text.ratingAverage, text.reviewCount]
  );

  if (context.reviewsToDisplay.length === 0) {
    return null;
  }

  return (
    <HotelShopSection className={clsx(block, className)} title={text.title}>
      {summary}
      <ul className={cn("review-list")}>
        {context.reviewsToDisplay.map((review) => (
          <li
            className={cn("review-item")}
            key={`${review.author}-${review.title}`}
          >
            <CustomerReviews review={review} />
          </li>
        ))}
      </ul>

      <Dialog>
        <DialogTrigger className={cn("more-reviews")}>
          {text.moreReviews}
        </DialogTrigger>
        <DialogContent title={text.title}>
          {summary}
          {context.reviews.categories.length > 0 ? (
            <ul className={cn("category-list")}>
              {context.reviews.categories.map((category) => {
                return (
                  <li
                    className={cn("category")}
                    key={`${category.categoryName}`}
                  >
                    <TripadvisorRatingIcon
                      scoreOutOfFive={category.score * 5}
                    />
                    {category.categoryName}
                  </li>
                );
              })}
            </ul>
          ) : null}
          <ul className={cn("review-list")}>
            {context.comments.map((review) => (
              <li
                className={cn("review-item")}
                key={`${review.author}-${review.title}`}
              >
                <CustomerReviews review={review} />
              </li>
            ))}
          </ul>
        </DialogContent>
      </Dialog>
      {children}
    </HotelShopSection>
  );
};
